import { useState } from "react";
import "@/App.css";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Separator } from "./components/ui/separator";
import {
  Phone,
  MapPin,
  Clock,
  Wrench,
  Car,
  Activity,
  ShieldCheck,
  Menu,
  X,
  Settings,
  Gauge,
  Facebook } from
"lucide-react";

const FACEBOOK_URL = "https://www.facebook.com/profile.php?id=61552292339172";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_45ee55ff-f106-45e8-978e-61447117460e/artifacts/pey2oony_ST_Autowerks_Facebook_Cover.png";

// Announcement Bar Component
const AnnouncementBar = () =>
<div
  className="announcement-bar py-2 px-4 text-center"
  data-testid="announcement-bar">

    <a
    href="tel:6093343318"
    className="text-white font-bold tracking-widest uppercase text-sm hover:underline"
    data-testid="announcement-phone-link">

      CALL 609-334-3318 FOR APPOINTMENTS
    </a>
  </div>;


// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  const navItems = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" }];


  return (
    <nav
      className="sticky top-0 z-50 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800"
      data-testid="navigation">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="#home" className="flex items-center" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="ST AUTOWERKS"
              className="h-12 w-auto" />

          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navItems.map((item) =>
            <a
              key={item.label}
              href={item.href}
              className="nav-link text-gray-300 hover:text-white font-medium tracking-widest uppercase text-sm"
              data-testid={`nav-link-${item.label.toLowerCase()}`}>

                {item.label}
              </a>
            )}
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-[#1877F2] text-white transition-colors duration-300 uppercase tracking-wider text-sm font-medium"
              data-testid="facebook-link-header">

              <Facebook className="w-5 h-5" />
              Facebook
            </a>
            <Button
              asChild
              className="bg-[#E63946] hover:bg-[#d62839] text-white rounded-none uppercase tracking-wider"
              data-testid="nav-call-button">

              <a href="tel:6093343318">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-300 hover:text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-button">

            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen &&
        <div className="md:hidden mobile-menu-enter pb-4" data-testid="mobile-menu">
            {navItems.map((item) =>
          <a
            key={item.label}
            href={item.href}
            className="block py-3 text-gray-300 hover:text-white font-medium tracking-wide uppercase text-sm border-b border-zinc-800"
            onClick={() => setIsOpen(false)}
            data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}>

                {item.label}
              </a>
          )}
            <Button
            asChild
            className="w-full mt-4 bg-[#E63946] hover:bg-[#d62839] text-white rounded-none uppercase tracking-wider"
            data-testid="mobile-nav-call-button">

              <a href="tel:6093343318">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </a>
            </Button>
            <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 py-3 text-gray-300 hover:text-[#E63946] font-medium"
            onClick={() => setIsOpen(false)}
            data-testid="mobile-facebook-link">

              <Facebook className="w-5 h-5" />
              Follow Us on Facebook
            </a>
          </div>
        }
      </div>
    </nav>);

};

// Hero Section
const HeroSection = () =>
<section
  id="home"
  className="hero-section min-h-screen flex items-center justify-center px-4"
  data-testid="hero-section">

    <div className="text-center max-w-4xl mx-auto">
      <img
      src={LOGO_URL}
      alt="ST AUTOWERKS"
      className="mx-auto mb-8 w-64 md:w-80 lg:w-96"
      data-testid="hero-logo" />

      <h1
      className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
      style={{ fontFamily: 'Oswald, sans-serif' }}
      data-testid="hero-title">

        Precision. Performance. Professionalism.
      </h1>
      <p
      className="text-xl md:text-2xl text-gray-300 mb-4 tracking-wide"
      data-testid="hero-subtitle">

        European • Domestic • Diagnostics
      </p>
      <p
      className="text-lg text-gray-400 mb-8"
      data-testid="hero-tagline">

        Built on Trust. Driven by Quality.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
        asChild
        size="lg"
        className="cta-button bg-[#E63946] hover:bg-[#d62839] text-white rounded-none uppercase tracking-wider text-lg px-8 py-6"
        data-testid="hero-cta-button">

          <a href="tel:6093343318">
            <Phone className="w-5 h-5 mr-2" />
            Call 609-334-3318
          </a>
        </Button>
        <Button
        asChild
        variant="outline"
        size="lg"
        className="border-2 border-white text-white hover:bg-white hover:text-black rounded-none uppercase tracking-wider text-lg px-8 py-6"
        data-testid="hero-secondary-button">

          <a href="#services">
            View Services
          </a>
        </Button>
      </div>
    </div>
  </section>;


// Services Section
const services = [
{
  icon: Car,
  title: "European Vehicles",
  description: "Expert service for BMW, Mercedes, Audi, Volkswagen, Porsche, and all European makes."
},
{
  icon: Wrench,
  title: "Domestic Vehicles",
  description: "Full service for Ford, Chevrolet, Dodge, GMC, and all American manufacturers."
},
{
  icon: Activity,
  title: "Advanced Diagnostics",
  description: "State-of-the-art diagnostic equipment to identify and resolve complex issues."
},
{
  icon: Settings,
  title: "Engine Repair",
  description: "Complete engine service, repair, and rebuilds for optimal performance."
},
{
  icon: ShieldCheck,
  title: "Preventive Maintenance",
  description: "Oil changes, brake service, fluid flushes, and scheduled maintenance."
},
{
  icon: Gauge,
  title: "Performance Upgrades",
  description: "Custom tuning, exhaust systems, and performance modifications."
}];


const ServicesSection = () =>
<section
  id="services"
  className="py-16 md:py-24 px-4 bg-zinc-950"
  data-testid="services-section">

    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2
        className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
        style={{ fontFamily: 'Oswald, sans-serif' }}
        data-testid="services-title">

          Our Services
        </h2>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Professional auto repair services for all makes and models
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) =>
      <Card
        key={index}
        className="service-card bg-zinc-900 border border-zinc-800 rounded-none"
        data-testid={`service-card-${index}`}>

            <CardContent className="p-8">
              <service.icon className="w-12 h-12 text-[#E63946] mb-4" />
              <h3
            className="text-xl font-bold text-white mb-3"
            style={{ fontFamily: 'Oswald, sans-serif' }}>

                {service.title}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {service.description}
              </p>
            </CardContent>
          </Card>
      )}
      </div>
    </div>
  </section>;


// About Section
const AboutSection = () =>
<section
  id="about"
  className="py-16 md:py-24 px-4 bg-zinc-900"
  data-testid="about-section">

    <div className="max-w-7xl mx-auto">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
          style={{ fontFamily: 'Oswald, sans-serif' }}
          data-testid="about-title">

            About <span className="text-[#E63946]">ST AUTOWERKS</span>
          </h2>
          <div className="space-y-4 text-gray-400 leading-relaxed">
            <p data-testid="about-paragraph-1">At ST AUTOWERKS, we bring years of experience and a passion for automotive excellence to every vehicle that enters our shop. Located in Woodbury Heights, NJ, we've built our reputation on delivering precision workmanship and honest service.

          </p>
            <p data-testid="about-paragraph-2">Whether you drive a European luxury vehicle or a domestic workhorse, our certified technicians have the expertise  to diagnose, repair, and maintain your car to the highest standards.

          </p>
            <p data-testid="about-paragraph-3">
              We believe in transparent communication, fair pricing, and getting the job done right the first time. Your satisfaction isn't just our goal—it's our commitment.
            </p>
          </div>
          <Separator className="my-6 bg-zinc-700" />
          <div className="flex flex-wrap gap-8">
            <div>
              <p className="text-3xl font-bold text-[#E63946]" style={{ fontFamily: 'Oswald, sans-serif' }}>15+</p>
              <p className="text-gray-400 text-sm uppercase tracking-wide">Years Experience</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#E63946]" style={{ fontFamily: 'Oswald, sans-serif' }}>5000+</p>
              <p className="text-gray-400 text-sm uppercase tracking-wide">Cars Serviced</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#E63946]" style={{ fontFamily: 'Oswald, sans-serif' }}>100%</p>
              <p className="text-gray-400 text-sm uppercase tracking-wide">Satisfaction</p>
            </div>
          </div>
        </div>
        <div className="relative">
          <img
          src="https://images.unsplash.com/photo-1625047509248-ec889cbff17f?auto=format&fit=crop&w=800&q=80"
          alt="Auto repair shop interior"
          className="w-full h-[400px] object-cover"
          data-testid="about-image" />

          <div className="absolute inset-0 border-2 border-[#E63946] transform translate-x-4 translate-y-4 -z-10"></div>
        </div>
      </div>
    </div>
  </section>;


// Contact Section
const ContactSection = () =>
<section
  id="contact"
  className="py-16 md:py-24 px-4 bg-zinc-950"
  data-testid="contact-section">

    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2
        className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
        style={{ fontFamily: 'Oswald, sans-serif' }}
        data-testid="contact-title">

          Contact Us
        </h2>
        <p className="text-gray-400 text-lg">
          Ready to get your vehicle serviced? Give us a call!
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Map */}
        <div className="h-[400px] bg-zinc-900 border border-zinc-800" data-testid="contact-map">
          <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3067.8!2d-75.15!3d39.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s438+Glassboro+Rd%2C+Woodbury+Heights%2C+NJ+08097!5e0!3m2!1sen!2sus!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="ST AUTOWERKS Location">
        </iframe>
        </div>

        {/* Contact Info */}
        <div className="space-y-8">
          {/* Address */}
          <div className="flex items-start gap-4" data-testid="contact-address">
            <div className="p-3 bg-[#E63946] text-white">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Address
              </h3>
              <p className="text-gray-400">
                438 Glassboro Rd<br />
                Woodbury Heights, NJ 08097
              </p>
            </div>
          </div>

          {/* Phone Numbers */}
          <div className="flex items-start gap-4" data-testid="contact-phone">
            <div className="p-3 bg-[#E63946] text-white">
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Phone
              </h3>
              <div className="space-y-2">
                <a
                href="tel:6093343318"
                className="block text-gray-400 hover:text-[#E63946] text-lg"
                data-testid="phone-link-1">

                  (609) 334-3318
                </a>
              </div>
            </div>
          </div>

          {/* Hours */}
          <div className="flex items-start gap-4" data-testid="contact-hours">
            <div className="p-3 bg-[#E63946] text-white">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Business Hours
              </h3>
              <div className="text-gray-400 space-y-1">
                <p><span className="text-white">Mon - Fri:</span> 9:00 AM - 5:00 PM</p>
                <p><span className="text-white">Saturday:</span> 9:00 AM - 2:00 PM</p>
                <p><span className="text-white">Sunday:</span> Closed</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <Button
          asChild
          size="lg"
          className="w-full cta-button bg-[#E63946] hover:bg-[#d62839] text-white rounded-none uppercase tracking-wider text-lg py-6"
          data-testid="contact-cta-button">

            <a href="tel:6093343318">
              <Phone className="w-5 h-5 mr-2" />
              Call For Appointment
            </a>
          </Button>

          {/* Social Media */}
          <div className="flex items-center gap-4 pt-4" data-testid="contact-social">
            <span className="text-gray-400 text-sm uppercase tracking-wide">Follow Us:</span>
            <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 bg-zinc-800 hover:bg-[#E63946] text-white transition-colors duration-300"
            data-testid="facebook-link-contact">

              <Facebook className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>;


// Footer
const Footer = () =>
<footer
  className="bg-black py-8 px-4 border-t border-zinc-800"
  data-testid="footer">

    <div className="max-w-7xl mx-auto text-center">
      <img
      src={LOGO_URL}
      alt="ST AUTOWERKS"
      className="mx-auto h-12 mb-4"
      data-testid="footer-logo" />

      
      {/* Social Media */}
      <div className="flex justify-center gap-4 mb-4">
        <a
        href={FACEBOOK_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="p-3 bg-zinc-900 hover:bg-[#E63946] text-white transition-colors duration-300"
        data-testid="facebook-link-footer">

          <Facebook className="w-5 h-5" />
        </a>
      </div>
      
      <p className="text-gray-500 text-sm">
        © {new Date().getFullYear()} ST AUTOWERKS. All rights reserved.
      </p>
      <p className="text-gray-600 text-xs mt-2">
        438 Glassboro Rd, Woodbury Heights, NJ 08097
      </p>
    </div>
  </footer>;


// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-zinc-950" data-testid="app-container">
      <AnnouncementBar />
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <AboutSection />
        <ContactSection />
      </main>
      <Footer />
    </div>);

}

export default App;